import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { es } from 'date-fns/locale';
import { Plus, Trash2, Target, CalendarIcon, TrendingUp, Loader2, Pencil } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MetasPage = () => {
  const { getAuthHeaders } = useAuth();
  const [metas, setMetas] = useState([]);
  const [gestores, setGestores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedMeta, setSelectedMeta] = useState(null);
  const [editingMeta, setEditingMeta] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [periodFilter, setPeriodFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    gestor_id: '',
    target_amount: '',
    target_tickets: '',
    period_type: 'monthly',
    period_start: new Date(),
    period_end: new Date()
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [metasRes, gestoresRes] = await Promise.all([
        axios.get(`${API_URL}/metas`, { headers }),
        axios.get(`${API_URL}/gestores`, { headers })
      ]);
      setMetas(metasRes.data);
      setGestores(gestoresRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodTypeChange = (type) => {
    const now = new Date();
    let start, end;
    
    switch(type) {
      case 'weekly':
        start = startOfWeek(now, { weekStartsOn: 1 });
        end = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'monthly':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'annual':
        start = startOfYear(now);
        end = endOfYear(now);
        break;
      default:
        start = now;
        end = now;
    }
    
    setFormData({
      ...formData,
      period_type: type,
      period_start: start,
      period_end: end
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        target_amount: parseFloat(formData.target_amount),
        target_tickets: parseInt(formData.target_tickets),
        period_type: formData.period_type,
        period_start: format(formData.period_start, 'yyyy-MM-dd'),
        period_end: format(formData.period_end, 'yyyy-MM-dd')
      };

      if (editingMeta) {
        await axios.put(`${API_URL}/metas/${editingMeta.id}`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Meta actualizada exitosamente');
      } else {
        payload.gestor_id = formData.gestor_id;
        await axios.post(`${API_URL}/metas`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Meta creada exitosamente');
      }
      fetchData();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar meta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (meta) => {
    setEditingMeta(meta);
    setFormData({
      gestor_id: meta.gestor_id,
      target_amount: meta.target_amount.toString(),
      target_tickets: meta.target_tickets.toString(),
      period_type: meta.period_type,
      period_start: new Date(meta.period_start),
      period_end: new Date(meta.period_end)
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/metas/${selectedMeta.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Meta eliminada');
      fetchData();
      setIsDeleteDialogOpen(false);
      setSelectedMeta(null);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingMeta(null);
    setFormData({
      gestor_id: '',
      target_amount: '',
      target_tickets: '',
      period_type: 'monthly',
      period_start: startOfMonth(new Date()),
      period_end: endOfMonth(new Date())
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  const getPeriodLabel = (type) => {
    switch(type) {
      case 'weekly': return 'Semanal';
      case 'monthly': return 'Mensual';
      case 'annual': return 'Anual';
      default: return type;
    }
  };

  const filteredMetas = periodFilter === 'all' 
    ? metas 
    : metas.filter(m => m.period_type === periodFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="metas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Metas de Ventas
          </h1>
          <p className="text-slate-500 mt-1">Establece y monitorea objetivos para tu equipo</p>
        </div>
        <div className="flex gap-3">
          <Select value={periodFilter} onValueChange={setPeriodFilter}>
            <SelectTrigger className="w-36" data-testid="metas-period-filter">
              <SelectValue placeholder="Filtrar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="weekly">Semanales</SelectItem>
              <SelectItem value="monthly">Mensuales</SelectItem>
              <SelectItem value="annual">Anuales</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={() => {
              handlePeriodTypeChange('monthly');
              setIsDialogOpen(true);
            }}
            className="bg-blue-700 hover:bg-blue-800"
            data-testid="add-meta-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Meta
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Target className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Metas Activas</p>
              <p className="text-2xl font-bold text-slate-900">{metas.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Cumplidas</p>
              <p className="text-2xl font-bold text-slate-900">
                {metas.filter(m => m.progress_amount >= 100).length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Target className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Promedio Progreso</p>
              <p className="text-2xl font-bold text-slate-900">
                {metas.length > 0 
                  ? Math.round(metas.reduce((sum, m) => sum + m.progress_amount, 0) / metas.length)
                  : 0}%
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Metas Grid */}
      {filteredMetas.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="p-8 text-center text-slate-500">
            No hay metas configuradas. Crea una nueva meta para comenzar.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMetas.map((meta) => (
            <Card key={meta.id} className="shadow-sm card-hover" data-testid={`meta-card-${meta.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold text-slate-900">
                      {meta.gestor_name}
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      {getPeriodLabel(meta.period_type)} • {meta.period_start} - {meta.period_end}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(meta)}
                      title="Editar meta"
                      data-testid={`edit-meta-${meta.id}`}
                    >
                      <Pencil className="w-4 h-4 text-blue-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedMeta(meta);
                        setIsDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-meta-${meta.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Amount Progress */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Meta de Ventas</span>
                    <span className="font-semibold text-slate-900">
                      {Math.round(meta.progress_amount)}%
                    </span>
                  </div>
                  <Progress 
                    value={Math.min(meta.progress_amount, 100)} 
                    className="h-2"
                  />
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>{formatCurrency(meta.current_amount)}</span>
                    <span>{formatCurrency(meta.target_amount)}</span>
                  </div>
                </div>

                {/* Tickets Progress */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Meta de Pasajes</span>
                    <span className="font-semibold text-slate-900">
                      {Math.round(meta.progress_tickets)}%
                    </span>
                  </div>
                  <Progress 
                    value={Math.min(meta.progress_tickets, 100)} 
                    className="h-2"
                  />
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>{meta.current_tickets} pasajes</span>
                    <span>{meta.target_tickets} pasajes</span>
                  </div>
                </div>

                {/* Status Badge - Sin "Necesita atención" */}
                <div className="pt-2">
                  {meta.progress_amount >= 100 ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ✓ Meta cumplida
                    </span>
                  ) : meta.progress_amount >= 75 ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Buen progreso
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      En progreso
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              {editingMeta ? 'Editar Meta' : 'Nueva Meta de Ventas'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Gestor - solo para crear */}
            {!editingMeta && (
              <div className="space-y-2">
                <Label>Gestor *</Label>
                <Select
                  value={formData.gestor_id}
                  onValueChange={(value) => setFormData({ ...formData, gestor_id: value })}
                >
                  <SelectTrigger className="bg-slate-50" data-testid="meta-gestor-select">
                    <SelectValue placeholder="Selecciona un gestor" />
                  </SelectTrigger>
                  <SelectContent>
                    {gestores.map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {editingMeta && (
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-sm text-slate-600">Gestor: <strong>{editingMeta.gestor_name}</strong></p>
              </div>
            )}

            {/* Period Type */}
            <div className="space-y-2">
              <Label>Tipo de Período *</Label>
              <Select
                value={formData.period_type}
                onValueChange={handlePeriodTypeChange}
              >
                <SelectTrigger className="bg-slate-50" data-testid="meta-period-type">
                  <SelectValue placeholder="Selecciona período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Semanal</SelectItem>
                  <SelectItem value="monthly">Mensual</SelectItem>
                  <SelectItem value="annual">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha Inicio</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start bg-slate-50" data-testid="meta-start-date">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(formData.period_start, 'dd/MM/yy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.period_start}
                      onSelect={(date) => date && setFormData({ ...formData, period_start: date })}
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Fecha Fin</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start bg-slate-50" data-testid="meta-end-date">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(formData.period_end, 'dd/MM/yy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.period_end}
                      onSelect={(date) => date && setFormData({ ...formData, period_end: date })}
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Targets */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Meta de Ventas (USD) *</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.target_amount}
                  onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                  placeholder="50000"
                  required
                  className="bg-slate-50"
                  data-testid="meta-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Meta de Pasajes *</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.target_tickets}
                  onChange={(e) => setFormData({ ...formData, target_tickets: e.target.value })}
                  placeholder="20"
                  required
                  className="bg-slate-50"
                  data-testid="meta-tickets-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={submitting || (!editingMeta && !formData.gestor_id)}
                className="bg-blue-700 hover:bg-blue-800"
                data-testid="save-meta-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingMeta ? 'Guardar Cambios' : 'Crear Meta')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar eliminación</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            ¿Estás seguro de que deseas eliminar esta meta para <strong>{selectedMeta?.gestor_name}</strong>? Esta acción no se puede deshacer.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              data-testid="confirm-delete-meta-btn"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MetasPage;
